import { usePredefinedMessage } from '@/container/daycare/daycareProfile/customisation/predefinedMessage/usePredefinedMessage'
import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Daycare, DaycarePredefinedMessageType } from '@/types/Daycare'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './confirmRequestSlot.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close } from '@mui/icons-material'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { CONFIRM_SLOT_REQUEST } from '@/modules/API/slot/requestSlotsHandler'
import TextArea from '@/components/_custom/TextArea/textArea'
import { ChildRequestSlot } from '@/types/Child'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import moment from 'moment/moment'
import { renderEventForRegularConflict } from '@/container/planning/utils/calendarEvents.tsx'
import { CalendarEventType } from '@/types/Calendar.ts'

interface ConfirmRequestSlotProps {
    open: boolean
    handleClose: () => void
    currentDaycare: Daycare
    refetchData: () => void
    closeParentDialog: () => void
    childRequestSlots: ChildRequestSlot[]
    accepted: boolean
}

const ConfirmRequestSlot: FunctionComponent<ConfirmRequestSlotProps> = ({
    open,
    currentDaycare,
    refetchData,
    closeParentDialog,
    childRequestSlots,
    handleClose,
    accepted
}: ConfirmRequestSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const {
        message: comment,
        textAreaProps,
        dialog
    } = usePredefinedMessage(
        accepted ? DaycarePredefinedMessageType.acceptRequestSlot : DaycarePredefinedMessageType.rejectRequestSlot,
        { daycare: currentDaycare }
    )

    const [selectChilds, setSelectChilds] = useState<string[]>(
        childRequestSlots.map((child) => {
            return child.slotId.toString()
        })
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: childRequestSlots.map((child: ChildRequestSlot): SelectOptionDeprecated => {
                return {
                    name:
                        child.childName +
                        (child.childComplement ? ' - ' + child.childComplement : '') +
                        ' - ' +
                        child.date,
                    value: child.slotId.toString()
                }
            })
        }
    ]

    function confirmAllRequestSlot(): void {
        childRequestSlots
            .filter((slot: ChildRequestSlot): boolean => selectChilds.includes(slot.slotId.toString()))
            .map((slot: ChildRequestSlot): void => {
                confirmRequestSlot({
                    variables: {
                        daycare_id: currentDaycare.id,
                        id: slot.slotId,
                        accepted: accepted,
                        comment: comment
                    }
                })
            })
    }

    const [confirmRequestSlot, { loading: loadingConfirm }] = useMutation(CONFIRM_SLOT_REQUEST, {
        onCompleted: () => {
            refetchData()
            closeParentDialog()

            showNotificationSuccess(accepted ? t('acceptRequestSlotSuccess') : t('refuseRequestSlotSuccess'))
        },
        onError: () => {
            showNotificationError(accepted ? t('errorAcceptedRequestSlot') : t('errorRefusedRequestSlot'))
        }
    })

    const title: string =
        childRequestSlots.length > 1
            ? accepted
                ? t('acceptRequestSlot')
                : t('refuseRequestSlot')
            : accepted
              ? t('acceptRegularRequest')
              : t('refuseRegularRequest')

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={title} />
            <DialogBody className={styles.DialogBody}>
                {childRequestSlots.length === 1 ? (
                    <>
                        <p className={styles.Text}>
                            {accepted ? t('acceptRegularRequestMessage') : t('refuseRegularRequestMessage')}
                        </p>
                        <div className={styles.UserInput}>
                            {renderEventForRegularConflict({
                                title: defineTitle(childRequestSlots[0]),
                                date: moment(childRequestSlots[0].date).toDate(),
                                start: moment(childRequestSlots[0].start, 'HH:mm:ss').toDate(),
                                end: moment(childRequestSlots[0].start, 'HH:mm:ss').toDate(),
                                slotId: childRequestSlots[0].slotId,
                                childId: childRequestSlots[0].childId,
                                type: CalendarEventType.REQUEST
                            })}
                        </div>
                    </>
                ) : (
                    <>
                        <p className={styles.Text}>
                            {accepted ? t('acceptRequestSlotMessage') : t('refuseRequestSlotMessage')}
                        </p>
                        <SelectMultiple
                            selectOptions={selectOptionsChild}
                            selectValues={selectChilds}
                            setSelectValues={setSelectChilds}
                            className={styles.UserInput}
                        />
                    </>
                )}
                <div className={styles.TextAreaInput}>
                    <p className={styles.TextAreaTitle}>{t('comment')}</p>
                    <TextArea placeholder={t('comment')} {...textAreaProps} className={styles.UserInput} />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Check />}
                        onClick={confirmAllRequestSlot}
                        className={styles.Button}
                        disabled={loadingConfirm}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
                {dialog}
            </DialogBody>
        </Dialog>
    )
}

export default ConfirmRequestSlot

function defineTitle(slot: ChildRequestSlot): string {
    return `${moment(slot.start, 'HH:mm:ss').format('HH:mm')} - ${moment(slot.end, 'HH:mm:ss').format('HH:mm')} - ${slot.date} - ${slot.childName}`
}
